import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Typography,
  Avatar,
} from '@mui/material';
import { ArrowBack as BackIcon, Edit as EditIcon, Shield as ProtectedIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/users/${id}`);
        if (!cancelled) setUser(r.data.data);
      } catch {
        if (!cancelled) toast.error('Failed to load user');
        if (!cancelled) navigate('/admin/users');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading || !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const groups = user.groups || user.Groups || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/users')}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, flex: 1 }}>
          View user
        </Typography>
        {hasPermission('users_modify') && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/admin/users/${id}/edit`)}
            sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
          >
            Edit
          </Button>
        )}
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              {user.first_name?.[0]}
              {user.last_name?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {user.first_name} {user.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Reference
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>
                {user.user_reference || '—'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Phone
              </Typography>
              <Typography variant="body1">{user.phone || '—'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Administrator
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                {user.is_admin ? <Chip label="Yes" size="small" color="primary" /> : <Typography variant="body2">No</Typography>}
                {user.is_system_admin && (
                  <Chip icon={<ProtectedIcon />} label="Protected" size="small" sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: '#f59e0b' }} />
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={user.is_active ? 'Active' : 'Inactive'}
                  size="small"
                  sx={{
                    bgcolor: user.is_active ? 'rgba(16,185,129,0.15)' : 'rgba(107,114,128,0.15)',
                    color: user.is_active ? '#10b981' : '#6b7280',
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Two-factor authentication
              </Typography>
              <Typography variant="body1">{user.otp_enabled ? 'Enabled' : 'Off'}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Groups & roles
          </Typography>
          {groups.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No groups assigned.
            </Typography>
          ) : (
            groups.map((g) => {
              const groupName = g.name || g.Name;
              const roles = g.roles || g.Roles || [];
              return (
                <Box key={g.id} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    {groupName}
                  </Typography>
                  {roles.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No roles on this group.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {roles.map((role) => (
                        <Box key={role.id} sx={{ pl: 1, borderLeft: 2, borderColor: 'divider' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {role.name}
                          </Typography>
                          {(role.Permissions || role.permissions || []).length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {(role.Permissions || role.permissions || []).slice(0, 12).map((perm) => (
                                <Chip key={perm.id} size="small" variant="outlined" label={perm.name || perm.code} />
                              ))}
                              {(role.Permissions || role.permissions || []).length > 12 && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`+${(role.Permissions || role.permissions || []).length - 12} more`}
                                />
                              )}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserDetail;
